import { useEffect } from 'react';
import { convertToError } from '@atlaskit/frontend-utilities/convert-to-error';
import {
	AtlasIcon,
	AtlassianAnalyticsIcon,
	AtlassianIcon,
	JiraIcon,
	BitbucketIcon,
	ConfluenceIcon,
	CompassIcon,
	TrelloIcon,
	JiraProductDiscoveryIcon,
	JiraServiceManagementIcon,
	JiraSoftwareIcon,
	JiraWorkManagementIcon,
	OpsgenieIcon,
	StatuspageIcon,
	FocusIcon,
	LoomIcon,
} from '@atlaskit/logo';
import { type ExternalUser } from '@atlaskit/user-picker';
import { getATLContextDomain } from '@atlassian/atl-context';
import { type SlackUser, FetchError as IntegrationsFetchError } from '@atlassian/integrations';
import {
	InviteProcessingResponseStatus,
	type Resource,
	type ResourceAri,
	type BadRequestErrorResponse,
	type BadRequesetErrorCodes,
	EMAIL_BLOCKED,
	REACHED_MAX_NUMBER_OF_EMAIL_ALIAS_SIGNUPS,
	FAILED_FETCH_ERROR_MESSAGE,
	NETWORK_ERROR_MESSAGE,
	HOST_NOT_FOUND_ERROR_MESSAGE,
	INVALID_DOMAIN_ERROR_GET_MESSAGE,
	INVALID_DOMAIN_POST_ERROR_MESSAGE,
	ERROR_KEY_NO_DEFAULT_GROUP,
	NO_DEFAULT_GROUP_ERROR_MESSAGE,
	type Product,
	type InviteApiFailureResponse,
	type FeaturedUserProvider,
	type FeaturedUser,
} from '../types';
import { GOOGLE_MAIL_DOMAINS, SLACK_SOURCE } from '../components/ThirdParty/constants';
import { type FailedFetchAttributes } from '../analytics';
import { FetchError } from '../clients/helpers';

const userNetworkFailureMessages = [
	FAILED_FETCH_ERROR_MESSAGE,
	NETWORK_ERROR_MESSAGE,
	HOST_NOT_FOUND_ERROR_MESSAGE,
];

const RECAPTCHA_SITE_KEY_PROD = '6LfOcIYhAAAAADBS5OnBllme06dW9vyW4Zgowiru';
const RECAPTCHA_SITE_KEY_STAGING = '6LebaYYhAAAAAKFQt-KH6vVkTJQqwZddT-HJCIM3';
const RECAPTCHA_SITE_KEY_DEV = '6Le_DxYiAAAAABV8BsFSj3AmZf3OkP9Fh9lfbZOZ';

const DEV_ENV_HOSTNAMES = [
	'localhost',
	'127.0.0.1',
	'0.0.0.0',
	'statlas.atl-paas.net',
	'atlaskit.atlassian.com',
];

export const adminHubManageUsersHref = (cloudId: string) => `${CLOUD_ADMIN_URL}/s/${cloudId}/users`;

export const adminHubManageUserHref = (cloudId: string, accountId: string) =>
	`${CLOUD_ADMIN_URL}/s/${cloudId}/users/${accountId}`;

export const isProd = (): boolean => (window.location.host.includes('-dev.com') ? false : true);

export const isDevEnv = () => DEV_ENV_HOSTNAMES.includes(window.location.hostname);

export const CLOUD_ADMIN_URL: string = `https://${getATLContextDomain('admin')}`;

export const extractDomainFromEmail = (email: string): string | null => {
	const parts = email.split('@');

	if (parts.length === 1) {
		return null;
	} else if (parts.length === 2 && !parts[0]) {
		return null;
	} else if (!parts[parts.length - 1]) {
		return null;
	}

	return parts[parts.length - 1];
};

export const makeAri = (productId: string, cloudId: string) =>
	`ari:cloud:${productId}::site/${cloudId}`;

// parsing logic from identity-common-frontend
export const parseAri = (resourceAri: ResourceAri): Resource => {
	const splittedAri = resourceAri.split(':');
	const resourceOwner = splittedAri[2];
	const resourceTypeIdPair = splittedAri[4];
	const [resourceType, ...resourceIdParts] = resourceTypeIdPair.split('/');
	const resourceId = resourceIdParts.join('/');
	return { resourceOwner, resourceType, resourceId };
};

export const getProductIdFromResource = (resourceAri: ResourceAri): string | undefined => {
	try {
		const { resourceOwner } = parseAri(resourceAri);
		return resourceOwner;
	} catch {
		return undefined;
	}
};

export const getCloudIdFromResource = (resourceAri: ResourceAri): string | undefined => {
	try {
		const { resourceId } = parseAri(resourceAri);
		return resourceId;
	} catch {
		return undefined;
	}
};

export const getFeaturedUsers = (
	provider: FeaturedUserProvider | undefined,
): Promise<FeaturedUser[] | undefined> => {
	if (!provider) {
		return Promise.resolve(undefined);
	}
	if (typeof provider === 'function') {
		const fn = provider as () => Promise<FeaturedUser[]>;
		return fn();
	}
	return Promise.resolve(provider);
};

export const ConfluenceProduct: Product = {
	id: 'confluence',
	label: 'Confluence',
	landingPath: '/wiki',
	directAccessLocation: 'confluence.core-invites',
	icon: ConfluenceIcon,
};

export const JiraProductDiscovery: Product = {
	id: 'jira-product-discovery',
	label: 'Jira Product Discovery',
	landingPath: '',
	directAccessLocation: 'jira.core-invites',
	icon: JiraProductDiscoveryIcon,
};

export const JiraWorkManagementProduct: Product = {
	id: 'jira-core',
	label: 'Jira Work Management',
	landingPath: '',
	directAccessLocation: 'jira.core-invites',
	icon: JiraWorkManagementIcon,
};

export const JiraWorkManagementProductAfterSporkRebrand: Product = {
	...JiraWorkManagementProduct,
	label: 'Jira',
	icon: JiraIcon,
};

export const JiraServiceManagementProduct: Product = {
	id: 'jira-servicedesk',
	label: 'Jira Service Management',
	landingPath: '',
	directAccessLocation: 'jira.core-invites',
	icon: JiraServiceManagementIcon,
};

export const JiraSoftwareProduct: Product = {
	id: 'jira-software',
	label: 'Jira Software',
	landingPath: '',
	directAccessLocation: 'jira.core-invites',
	icon: JiraSoftwareIcon,
};

export const JiraSoftwareProductAfterSporkRebrand: Product = {
	...JiraSoftwareProduct,
	label: 'Jira',
	icon: JiraIcon,
};

export const JiraProduct: Product = {
	id: 'jira',
	label: 'Jira',
	landingPath: '',
	directAccessLocation: 'jira.core-invites',
	icon: JiraIcon,
};
export const TrelloProduct: Product = {
	id: 'trello',
	label: 'Trello',
	landingPath: '',
	directAccessLocation: 'others.core-invites',
	icon: TrelloIcon,
};
export const BitbucketProduct: Product = {
	id: 'bitbucket',
	label: 'Bitbucket',
	landingPath: '',
	directAccessLocation: 'others.core-invites',
	icon: BitbucketIcon,
};
export const StatusPageProduct: Product = {
	id: 'statuspage',
	label: 'Statuspage',
	landingPath: '',
	directAccessLocation: 'others.core-invites',
	icon: StatuspageIcon,
};
export const OpsgenieProduct: Product = {
	id: 'opsgenie',
	label: 'Opsgenie',
	landingPath: '',
	directAccessLocation: 'others.core-invites',
	icon: OpsgenieIcon,
};

export const CompassProduct: Product = {
	id: 'compass',
	label: 'Compass',
	landingPath: '/compass',
	directAccessLocation: 'others.core-invites',
	icon: CompassIcon,
};

export const AtlasProduct: Product = {
	id: 'townsquare',
	label: 'Atlas',
	landingPath: '',
	directAccessLocation: 'others.core-invites',
	icon: AtlasIcon,
};

export const AnalyticsProduct: Product = {
	id: 'avp',
	label: 'Atlassian Analytics',
	landingPath: '/analytics',
	directAccessLocation: 'others.core-invites',
	icon: AtlassianAnalyticsIcon,
};
export const JiraProductDiscoveryProduct: Product = {
	id: 'jira-product-discovery',
	label: 'Jira Product Discovery',
	landingPath: '',
	directAccessLocation: 'others.core-invites',
	icon: JiraProductDiscoveryIcon,
};

export const FocusProduct: Product = {
	id: 'mercury',
	label: 'Focus',
	landingPath: '/focus',
	directAccessLocation: 'others.core-invites',
	icon: FocusIcon,
};

export const LoomProduct: Product = {
	id: 'loom',
	label: 'Loom',
	landingPath: '',
	directAccessLocation: 'others.core-invites',
	icon: LoomIcon,
};

const products: Product[] = [
	ConfluenceProduct,
	JiraWorkManagementProductAfterSporkRebrand,
	JiraServiceManagementProduct,
	JiraSoftwareProductAfterSporkRebrand,
	JiraProduct,
	TrelloProduct,
	BitbucketProduct,
	StatusPageProduct,
	OpsgenieProduct,
	CompassProduct,
	AtlasProduct,
	AnalyticsProduct,
	JiraProductDiscoveryProduct,
	FocusProduct,
	LoomProduct,
];

export const getBillingUrl = (products: string[], cloudId: string): string => {
	const baseUrl = `${CLOUD_ADMIN_URL}/s/${cloudId}`;
	return products.length === 1
		? `${baseUrl}/billing/applications/change-edition/${products[0]}.ondemand`
		: `${baseUrl}/billing-preview`;
};

export const isProductIdWhiteListed = (productId: string): boolean =>
	!!products.find((p) => productId === p.id);

export const getProductById = (productId: string) => products.find((p) => productId === p.id);

export const getProductTitle = (productId: string | undefined): string => {
	const foundProduct = products.find((p) => productId === p.id);
	return foundProduct ? foundProduct.label : productId || 'Atlassian';
};

export const getProductTitleFromAri = (resourceAri: ResourceAri): string => {
	const productId = getProductIdFromResource(resourceAri);

	return getProductTitle(productId || '');
};

export const getProductId = (product: string): string => {
	const foundProduct = products.find((p) => product === p.label);
	return foundProduct ? foundProduct.id : product;
};

export const getProductIcon = (productId: string) => {
	const foundProduct = products.find((p) => productId === p.id);
	return foundProduct ? foundProduct.icon : AtlassianIcon;
};

export const getLandingPathByProduct = (productId: string): string => {
	const foundProduct = products.find((p) => productId === p.id);
	return foundProduct ? foundProduct.landingPath : '';
};

// This function is used to get the 'location' required for the direct access post endpoint.
export const getDirectAccessLocation = (productId: string): string => {
	const foundProduct = products.find((p) => productId === p.id);
	return foundProduct ? foundProduct.directAccessLocation : 'others.core-invites';
};

export const getInstanceUrl = (): string => {
	return window.location.origin;
};

export const normalizeJiraSubProduct = (subProduct: string) => `jira-${subProduct.toLowerCase()}`;

export const isMobile = () => {
	return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const isEnglishLocale = (locale: string): boolean => {
	return (locale || '').toLowerCase().startsWith('en');
};

export function Buggy() {
	useEffect(() => {
		throw new Error('Something wrong inside Invite people.');
	});

	return null;
}

export const getUniqueEmailDomains = (emails: string[]): string[] => {
	const domains = new Set<string>();
	emails.forEach((email) => {
		const domain = extractDomainFromEmail(email);
		if (domain) {
			domains.add(domain);
		}
	});
	return Array.from(domains);
};

export enum ResultStatus {
	FULFILLED = 'fulfilled',
	FAILED = 'failed',
}

export type Result<T, R = void> = FulfilledResult<T | R | void> | RejectedResult<R | void>;

export type FulfilledResult<T> = {
	status: ResultStatus.FULFILLED;
	value: T;
};

export type RejectedResult<R> = {
	status: ResultStatus.FAILED;
	errorDetails: R;
};

const markFulfilled = <T>(value: T): FulfilledResult<T> => ({
	status: ResultStatus.FULFILLED,
	value,
});

const markRejected = <R>(errorDetails: R): RejectedResult<R> => ({
	status: ResultStatus.FAILED,
	errorDetails,
});

/**
 * Will wait for all promises to resolve or reject, wrapping their real results in
 * object containing the status so it's easy to filter it later. Loosely inspired by
 * [Promise.allSettled](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/allSettled)
 * which can replace this implementation once it makes to the browsers.
 * @param promises
 */
export const waitForAllPromises = <T, R = void>(
	promises: Promise<T | R>[],
): Promise<(FulfilledResult<T> | RejectedResult<R>)[]> => {
	return Promise.all(
		promises.map((result) =>
			result
				.then((successResponse: T | R) => markFulfilled<T>(successResponse as T))
				.catch(markRejected),
		),
	);
};

// extends unknown is to avoid type error when using Generics with arrow functions
// mutates cache reference, will clear once it gets too large.
export const tryCacheWithClear = <Type extends unknown>(
	cache: Map<string, Type>,
	key: string,
	value: Type,
	size: number = 50,
): void => {
	if (cache.size >= size) {
		cache.clear();
	}

	cache.set(key, value);
};

export const BAD_REQUEST = 'bad-request';
export const LICENSE_EXCEEDED = 'licence-exceeded';

export const shouldReportFailedInvite = (response: InviteApiFailureResponse) => {
	const { code, message } = response;
	if (code === BAD_REQUEST && message === 'User cannot invite themselves') {
		return false;
	}

	if (code === LICENSE_EXCEEDED) {
		return false;
	}

	return true;
};
export const isUserNetworkFailure = (errorMessage: string) =>
	userNetworkFailureMessages.some((failureMessage) => errorMessage?.includes(failureMessage));

export const isForbidden = (statusCode: number | undefined) => {
	return statusCode === 403;
};

export const isMessageErrorType = (message: string, error: BadRequesetErrorCodes) => {
	try {
		const badRequestResponse: BadRequestErrorResponse = JSON.parse(message);
		return badRequestResponse.some((val) => val.errorCodes.userName.includes(error));
	} catch {
		return false;
	}
};

export const isNoDefaultGroup = (errorKey: string | undefined, errorMessage: string) =>
	errorKey === ERROR_KEY_NO_DEFAULT_GROUP || errorMessage === NO_DEFAULT_GROUP_ERROR_MESSAGE;

export const isAcceptableFailureForUserInvite = (response: InviteApiFailureResponse) => {
	const { key, message, status } = response;
	return (
		isAcceptableFailure(message, status) ||
		(status === 400 &&
			(isMessageErrorType(message, EMAIL_BLOCKED) ||
				isMessageErrorType(message, REACHED_MAX_NUMBER_OF_EMAIL_ALIAS_SIGNUPS))) ||
		(status === 412 && isNoDefaultGroup(key, message))
	);
};

export const isAcceptableFailureForDirectAccess = (
	errorMessage: string,
	statusCode: number | undefined,
) =>
	(statusCode === 400 &&
		(errorMessage === INVALID_DOMAIN_ERROR_GET_MESSAGE ||
			errorMessage.startsWith(INVALID_DOMAIN_POST_ERROR_MESSAGE))) ||
	isForbidden(statusCode);

export const isAcceptableFailure = (errorMessage: string, statusCode: number | undefined) =>
	(!statusCode && isUserNetworkFailure(errorMessage)) || statusCode === 401;

export const resolveInviteProcessingResponseStatus = (status: InviteProcessingResponseStatus) => {
	switch (status) {
		case InviteProcessingResponseStatus.SUCCESS:
			return true;
		case InviteProcessingResponseStatus.ERROR:
			return false;
		case InviteProcessingResponseStatus.SKIPPED:
			return undefined;
	}
};

export const hasGmailDomain = (loggedInEmailDomain: string | undefined) =>
	Boolean(loggedInEmailDomain && GOOGLE_MAIL_DOMAINS.includes(loggedInEmailDomain));

export const resolveError = (errorResponse: unknown): FailedFetchAttributes => {
	if (errorResponse instanceof FetchError) {
		return {
			error: errorResponse,
			errorMessage: errorResponse.message,
			responseCode: errorResponse.responseCode,
		};
	}
	if (errorResponse instanceof IntegrationsFetchError) {
		return {
			error: errorResponse,
			errorMessage: errorResponse.message,
			responseCode: errorResponse.statusCode,
		};
	}
	if (errorResponse instanceof Error) {
		return { error: errorResponse, errorMessage: errorResponse.message };
	}
	const error = convertToError(errorResponse);
	return { error, errorMessage: error.message };
};

export const getRecaptchaSiteKey = () =>
	isDevEnv()
		? RECAPTCHA_SITE_KEY_DEV
		: isProd()
		? RECAPTCHA_SITE_KEY_PROD
		: RECAPTCHA_SITE_KEY_STAGING;

export const chunk = <T>(arr: T[], size: number): T[][] => {
	const chunks: T[][] = [];
	let index = 0;
	do {
		const nextIndex = index + size;
		chunks.push(arr.slice(index, nextIndex));
		index = nextIndex;
	} while (index < arr.length);
	return chunks;
};

export const slackToExternalUser = (slackUser: SlackUser): ExternalUser => ({
	id: slackUser.id,
	email: slackUser.email,
	name: slackUser.name,
	publicName: slackUser.displayName,
	avatarUrl: slackUser.avatarUrl,
	sources: [SLACK_SOURCE],
	type: 'user',
	isExternal: true,
});

export const removeZeroWidthCharacters = (s: string): string => s.replace(/[\u200B-\u200D\uFEFF]/g, '');

export const getBooleanLocalStorageKey = (key: string) => {
	const value = getLocalStorageKey(key);

	if (!value) {
		return null;
	}

	if (!['true', 'false'].includes(value)) {
		removeLocalStorageKey(key);

		return null;
	}

	return value === 'true';
};

export const getLocalStorageKey = (key: string) => {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
};

export const updateLocalStorageKey = (key: string, value: string) => {
	try {
		localStorage.setItem(key, value);
	} catch {
		return null;
	}
};

export const removeLocalStorageKey = (key: string): void => {
	try {
		localStorage.removeItem(key);
	} catch {
		return;
	}
};

export const adminManageUserInviteLinkHref = (orgId: string) =>
	`${CLOUD_ADMIN_URL}/o/${orgId}/user-access-settings`;

export const serializeError = (error: unknown): string => {
	if (error instanceof Error) {
		return error.message || 'Unknown error';
	}

	switch (typeof error) {
		case 'string':
			return error;
		case 'object':
			try {
				return JSON.stringify(error);
			} catch {
				return 'Unknown error';
			}
		default:
			return String(error).trim();
	}
};
